import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnyFieldApi } from '@tanstack/react-form';
import { IssueVisibility } from '@momentum/types';
import { Users, Lock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { FieldInfo } from '@/shared/components/form/FieldInfo';
import { cn } from '@/shared/utils/utils';

interface VisibilitySelectorProps {
  field: AnyFieldApi;
  disabled?: boolean;
  onVisibilityChange?: (visibility: IssueVisibility) => void;
}

export const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  field,
  disabled,
  onVisibilityChange,
}) => {
  const { t } = useTranslation('goals');

  const currentValue = field.state.value as IssueVisibility;
  const isPublic = currentValue === IssueVisibility.PUBLIC;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-900">
        {t('privacy')}
      </label>
      <Select
        value={currentValue}
        onValueChange={(value) => {
          field.handleChange(value);
          onVisibilityChange?.(value as IssueVisibility);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(field.state.meta.errors.length > 0 && 'border-red-500')}
        >
          <div className="flex items-center gap-2 flex-1">
            {isPublic ? (
              <Users className="h-4 w-4 text-gray-600 shrink-0" />
            ) : (
              <Lock className="h-4 w-4 text-gray-600 shrink-0" />
            )}
            <SelectValue placeholder={t('privacy')}>
              {isPublic ? t('public') : t('private')}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={IssueVisibility.PUBLIC}>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-sm">{t('public')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('publicDescription')}
                </span>
              </div>
            </div>
          </SelectItem>
          <SelectItem value={IssueVisibility.PRIVATE}>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-sm">{t('private')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('privateDescription')}
                </span>
              </div>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      <FieldInfo field={field} />
    </div>
  );
};
